import React, { useState, useEffect, useRef, useMemo } from 'react';

import { Typography, Dropdown } from '@hpi-sox/hpstellar-core';
import { Tooltip } from '@hpi-sox/hpstellar-core/visId';

import withPageComponents from '../../../page/components/with-page-components';
import ScrollContainer from './scroll-container';
import StickyCard from './sticky-card';
import GameFPSModal from './game-fps-modal';
import ETRApi from '../../../core/src/etr-wcs-api-client';
import { filterGamesByFpsThreshold, pickFPS, graphicsPresets } from './util';

import './game-fps.less';

const FPS_SELECTION_ID = 'GAMEFPS_SELECT';
const FPS_SPEC_CATEGORY_TITLES = new Set([
    'processor',
    'graphicscard',
    'processorandgraphics',
    'processorgraphicandmemory',
]);
const normalizeSpecTitle = title => (title || '').toLowerCase().replace(/[^a-z0-9]+/g, '');

const hasValidPreset = game => {
    const { preset } = game || {};
    if (!preset || typeof preset !== 'object') {
        return false;
    }
    // Check if game has at least one preset value
    return Object.values(preset).some(value => value != null && value !== '');
};

const getFpsHardware = baseConfig => {
    const { specCategories = [], tabs = [], DisplayFPSCalculator } = baseConfig || {};
    if (!DisplayFPSCalculator || specCategories.length < 1) {
        return [];
    }

    const selectedParts = specCategories.reduce((result, category) => {
        const { title, optionitems = [], display, path } = category || {};
        const isTargetCategory = FPS_SPEC_CATEGORY_TITLES.has(normalizeSpecTitle(title));
        if (!isTargetCategory) {
            return result;
        }

        const isVisible = path && tabs.length > 0 ? tabs.includes(path) : display === 'true';
        if (!isVisible) {
            return result;
        }

        const selectedOption = optionitems.find(option => option?.selected === 'true' && option?.name);
        if (selectedOption?.name) {
            result.push(selectedOption.name);
        }
        return result;
    }, []);

    return selectedParts.length > 0 ? [selectedParts.join(', ')] : [];
};

const GameFPS = ({ baseConfig, skuList, slim = false }) => {
    const { DisplayFPSCalculator } = baseConfig || {};
    const displayFPSCalc = !!DisplayFPSCalculator;
    const [graphicsPreset, setGraphicsPreset] = useState(graphicsPresets[0]);
    const [selecting, setSelecting] = useState(false);
    const [selection, setSelection] = useState([]);
    const [gameList, setGameList] = useState([]);
    const [gameTitles, setGameTitles] = useState({});
    const [fpsData, setFpsData] = useState({});
    const hasInitialPrefetchRunRef = useRef(false);
    //tracks the preset+hardware combination last fetched, so the preset effect only skips a genuine duplicate, not the next arbitrary run
    const fetchedFpsSignatureRef = useRef(null);
    const hardware = getFpsHardware(baseConfig);
    const hardwareString = (hardware || []).join('|');
    const filteredGameList = useMemo(() => filterGamesByFpsThreshold(gameList, fpsData), [gameList, fpsData]);
    const filteredGameIds = new Set(filteredGameList.map(game => game.id));
    const filteredSelection = selection.filter(game => filteredGameIds.has(game.id));

    //shared fetch used by both the preset-change effect and the Calculate CTA so results are applied consistently
    const fetchFpsForGames = useMemo(
        () => (games, preset, hardwareParts) => {
            const gameIds = games.map(game => `${game.id}`).join(',');
            const presets = games.reduce((result, game) => {
                result[game.id] = game.preset?.[preset] || preset;
                return result;
            }, {});
            return ETRApi.cto.hpGameFPS({ gameIds, hardware: hardwareParts, presets }).then(resp => {
                const { gameTitles: updatedGameTitles, predictions } = resp || {};
                if (updatedGameTitles) {
                    setGameTitles(prev => ({ ...prev, ...updatedGameTitles }));
                }
                if (Array.isArray(predictions)) {
                    const updatedFpsData = predictions.reduce((result, { game, fps }) => {
                        if (game?.id && fps) {
                            result[game.id] = {
                                ...fps,
                                ...(game.resolution && { resolution: game.resolution }),
                            };
                        }
                        return result;
                    }, {});
                    setFpsData(prev => ({ ...prev, ...updatedFpsData }));
                }
            });
        },
        [],
    );
    useEffect(() => {
        //fetch the game list
        ETRApi.cto.gameList().then((gameList = []) => {
            // Filter out games without valid preset values to prevent API errors
            const filteredGameList = gameList.filter(hasValidPreset);
            setGameList(filteredGameList);

            if (sessionStorage.getItem(FPS_SELECTION_ID)) {
                const storedSelection = JSON.parse(sessionStorage.getItem(FPS_SELECTION_ID));
                const restoredSelection = storedSelection
                    .map(stored => filteredGameList.find(g => g.id === stored.id))
                    .filter(Boolean)
                    .slice(0, 5);
                setSelection(restoredSelection.length > 0 ? restoredSelection : filteredGameList.slice(0, 3));
            } else {
                setSelection(filteredGameList.slice(0, 3));
            }

            // Pre-fetch FPS for all games so modal shows values immediately on open
            if (filteredGameList.length > 0) {
                const allGameIds = filteredGameList.map(game => `${game.id}`).join(',');
                const allPresets = filteredGameList.reduce((result, game) => {
                    result[game.id] = game.preset?.[graphicsPreset] || graphicsPreset;
                    return result;
                }, {});
                if (!hasInitialPrefetchRunRef.current) {
                    hasInitialPrefetchRunRef.current = true;
                    if (displayFPSCalc && (hardware || []).length > 0) {
                        fetchedFpsSignatureRef.current = `${graphicsPreset}|${hardwareString}`;
                    }
                    ETRApi.cto
                        .hpGameFPS({ gameIds: allGameIds, hardware: getFpsHardware(baseConfig), presets: allPresets })
                        .then(respData => {
                            const { gameTitles: updatedGameTitles, predictions } = respData || {};
                            if (updatedGameTitles) setGameTitles(updatedGameTitles);
                            if (Array.isArray(predictions)) {
                                const updatedFpsData = predictions.reduce((result, { game, fps }) => {
                                    if (game?.id && fps)
                                        result[game.id] = {
                                            ...fps,
                                            ...(game.resolution && { resolution: game.resolution }),
                                        };
                                    return result;
                                }, {});
                                setFpsData(prev => ({ ...prev, ...updatedFpsData }));
                            }
                        });
                }
            }
        });
    }, []);

    useEffect(() => {
        if (displayFPSCalc && gameList.length > 0 && (hardware || []).length > 0) {
            const currentSignature = `${graphicsPreset}|${hardwareString}`;
            if (fetchedFpsSignatureRef.current === currentSignature) {
                return;
            }
            fetchedFpsSignatureRef.current = currentSignature;
            try {
                // Always pass all filtered game IDs for preset selection
                fetchFpsForGames(gameList, graphicsPreset, hardware);
            } catch (e) {
                console.log(e);
            }
        }
        //gameList.length is required so this re-evaluates once the async game list resolves (e.g. preset changed before load completed)
    }, [displayFPSCalc, hardwareString, graphicsPreset, gameList.length]);

    const itemClick = id => {
        setSelecting(true);
    };

    const fill = selected => {
        const fillSelection = Array(5).fill({});

        return fillSelection.map((item, index) => {
            if (index < selected.length) {
                return selected[index];
            }
            return {};
        });
    };

    const onSelection = selected => {
        sessionStorage.setItem(FPS_SELECTION_ID, JSON.stringify(selected));
        setSelection(selected);
        //ensure any newly selected game missing FPS data is fetched, since Calculate doesn't otherwise trigger the preset effect
        //avgFps can legitimately be 0, so check for null/undefined rather than falsy
        const missingFpsGames = selected.filter(game => fpsData[game.id]?.avgFps == null);
        if (displayFPSCalc && (hardware || []).length > 0 && missingFpsGames.length > 0) {
            fetchFpsForGames(missingFpsGames, graphicsPreset, hardware);
        }
    };

    const onRemove = id => {
        //when the carousel is full, StickyCard shows this action as "CHANGE" - remove the game and reopen the modal so another can be picked
        const wasFull = selection.length >= 5;
        const newSelection = selection.filter((item, i) => i !== id);
        setSelection(newSelection);
        sessionStorage.setItem(FPS_SELECTION_ID, JSON.stringify(newSelection));
        if (wasFull) {
            setSelecting(true);
        }
    };

    const onModalClose = () => {
        setSelecting(false);
    };

    if (!displayFPSCalc) {
        return null;
    }

    const presetLabel = (
        <div className="presetLabel">
            <Typography className="subtitle" variant="boldXS" style={{ color: '#2C3038' }}>
                Graphics Preset
            </Typography>
            <Tooltip
                content="FPS estimates are based on vendor benchmarks and may vary based on system configuration, game settings, and other factors."
                position="top-start"
                title="FPS estimates"
                tooltipClassName="fps-toolip-msg"
                translations={{
                    closeBtnLabel: 'Close tooltip',
                }}
                triggerButtonProps={{
                    'aria-label': 'Trigger Button',
                }}
            />
        </div>
    );

    return (
        <div className={`gameFPSContainer${slim ? ' slim' : ''}`}>
            <div className="gameLeft">
                {slim ? (
                    presetLabel
                ) : (
                    <>
                        <Typography variant="bodyL" style={{ color: '#2C3038' }}>
                            Real FPS Performance in your build
                        </Typography>
                        {presetLabel}
                    </>
                )}
                <Dropdown
                    defaultValue={graphicsPreset}
                    onChange={setGraphicsPreset}
                    options={[
                        {
                            gtmSort: 'lowFps',
                            label: 'Low',
                            value: graphicsPresets[0],
                        },
                        {
                            gtmSort: 'mediumFps',
                            label: 'Medium',
                            value: graphicsPresets[1],
                        },
                        {
                            gtmSort: 'highFps',
                            label: 'High',
                            value: graphicsPresets[2],
                        },
                    ]}
                    style={{
                        maxWidth: 206,
                    }}
                    customLabel=""
                />
            </div>

            <ScrollContainer className="gameRight" slim={slim} key={slim ? 'slim' : 'full'}>
                {fill(filteredSelection).map((item, index) => (
                    <StickyCard
                        {...item}
                        title={item.name}
                        imgUrl={item.imageUrl}
                        fps={
                            fpsData[item.id]?.avgFps != null
                                ? `${fpsData[item.id].avgFps} FPS`
                                : pickFPS(item, gameTitles, graphicsPreset)
                        }
                        index={index}
                        key={index}
                        onClick={itemClick}
                        onRemove={onRemove}
                        slim={slim}
                        isFull={selection.length >= 5}
                    />
                ))}
            </ScrollContainer>
            <GameFPSModal
                gameList={filteredGameList}
                gameTitles={gameTitles}
                fpsData={fpsData}
                selecting={selecting}
                selection={selection}
                onSelection={onSelection}
                onModalClose={onModalClose}
                graphicsPreset={graphicsPreset}
            />
        </div>
    );
};

export default withPageComponents(GameFPS, {
    components: ['baseConfig', 'skuList'],
    device: true,
});
